(ns ycweb.components.history
  (:require
    [clojure.set :as set]
    [ycweb.util.core :as u]
    [ycweb.util.comms :as comms]
    [ajax.core :as ajax]))



(defn map-diff
  ([o n]
   (map-diff [] 0 o n))
  ([l o n]
   (map-diff [] l o n))
  ([p l o n]
   (let [old-keys (set (keys o))
         new-keys (set (keys n))

         added-keys (set/difference new-keys old-keys)
         removed-keys (set/difference old-keys new-keys)
         same-keys (set/intersection new-keys old-keys)
         path (memoize #(conj p %))]
     (->>
       (concat
         (map (fn [k] [:a (path k) (get n k)]) added-keys)
         (map (fn [k] [:d (path k)]) removed-keys)

         (mapcat
           (fn [k]
             (cond
               (and (pos? l) (map? (get n k)) (map? (get o k)))
               (map-diff (path k) (dec l) (get o k) (get n k))

               (not= (get o k) (get n k))
               [[:u (path k) (get n k)]]))
           same-keys))
       (remove nil?)))))

(defn replay-diffs [root diffs]
  (reduce
    (fn [s {:keys [changes]}]
      (reduce
        (fn [state [op k v]]
          (case op
            :a (assoc-in state k v)
            :d (u/dissoc-in state k)
            :u (assoc-in state k v)))
        s changes))
    root diffs))

(defn send-snapshot [app-snapshot & [finish-fn]]
  ;; Take a stable copy and fire it off for logging purposes (not error debug)
  (let [{:keys [comms-index comms-snapshot comms-send-disabled diffs]} @app-snapshot
        ;; Setup a new fast-forward snapshot
        new-diffs (drop comms-index diffs)
        new-snapshot (replay-diffs comms-snapshot new-diffs)

        data
        (pr-str
          {:snapshot
           {:snapshot comms-snapshot
            :diffs new-diffs}
           :deviceid (comms/deviceid)
           :clientid (comms/clientid)
           ;; oh dear - json doesn't handle this particularly well, but ajax/POST not working correctly
           ;; with :edn yet.. gotta get that figured out (hence the pr-str above)
           :send-date (u/now)})]
    (if (and (-> new-snapshot :opts :send-history) (not comms-send-disabled))
      ;; No diffs, simply fire 'success', but don't bother sending anything.
      (cond
        (empty? new-diffs)
        (if finish-fn (finish-fn))

        ;(> (count data) 850000)
        ;(do
        ;  (println "App state too large, not sending.")
        ;  (if finish-fn (finish-fn)))

        :else
        (ajax/POST "/pos/capture-snapshot"
          {:format :json
           :params
           {:data data}
           :headers
           {"Accept" "application/json"
            "x-csrf-token" (:csrf-token comms-snapshot)}
           :handler
           (fn [response]
             ;; Store the fast-forwarded snapshot, do an actual addition in case comm-index has been altered
             (swap! app-snapshot
               (fn [s]
                 (assoc s
                   :comms-index (+ (:comms-index s) (count new-diffs))
                   :comms-snapshot new-snapshot)))
             (if finish-fn (finish-fn)))
           :error-handler
           (fn [_]
             (println "History comms failure.. oh well. ¯\\_(ツ)_/¯")

             (if finish-fn (finish-fn)))}))

      ;; If history sending is disabled, lets just pretend it worked, so that GC can do its thing.
      (do
        (println "Skipping history comms.")
        (swap! app-snapshot
          (fn [s]
            (assoc s
              :comms-index (+ (:comms-index s) (count new-diffs))
              :comms-snapshot new-snapshot)))))))


;; NOTE: there is a possibility here that if we send a chunk of history, but it doesn't
;; get cleared properly on the client side, it gets sent in duplicate.
;; To prevent this, we'd need to tag a uuid onto the diff so that we can de-dup when we need
;; to finally collate states. I'm choosing to just have this as an issue for now, since the
;; material impact is very low compared to the overall extra bandwidth overhead of uuid's on every
;; single change.
;;
;; But I'm leaving this note here in case I need to kick myself from the future, at least I know
;; I made the decision deliberately.
(defn create-diff [s o n]
  {:date (u/now)
   :changes (map-diff 1 o n)})

(declare clear-history-to-index)
(defn record-history [app-snapshot k r o n]
  (when
    (and
      (not= o n)
      (not (:restoring @app-snapshot))
      (not (:history-recording-disabled @app-snapshot))
      (= (:index @app-snapshot) (count (:diffs @app-snapshot))))

    (swap! app-snapshot
      (fn [{:keys [snapshot diffs index] :as app}]
        (if (nil? snapshot)
          (merge
            app
            {:snapshot n
             :comms-snapshot n
             :diffs [(create-diff 1 n n)]
             :index 1})

          (let [newdiff (create-diff (inc (count diffs)) o n)]
            (merge
              app
              {:snapshot snapshot
               :diffs (conj (vec diffs) newdiff)
               :index (if (= index (count diffs)) (inc index) index)})))))

    ;; When there's some gains to be made compacting the history, do so.


    ;; When there's no change to the state within an acceptable timeframe,
    ;; ship it off to the server
    #?(:cljs
       (.setTimeout js/window
         (fn []
           ;; Send the snapshot
           (when (= @r n)
             (send-snapshot app-snapshot)

             ;; Perform a bit of cleanup, just so this never gets out of hand.
             (let [{:keys [diffs comms-index]} @app-snapshot
                   size (count diffs)]
               (println "Checking history GC: " size ", commsindex: " comms-index)
               (when
                 (and (> size 200))
                 (println "GC history to index: " (min comms-index (- size 100)))
                 (clear-history-to-index app-snapshot (min comms-index (- size 100)))))))
         3000))))




;; ======= History Management
(defn clear-history [app-snapshot app-atom]
  (swap! app-atom assoc-in [:dev :locked] false)

  (reset! app-snapshot
    {:snapshot @app-atom
     :comms-snapshot @app-atom
     :diffs [(create-diff 1 {} {})]
     :index 0
     :comms-index 0}))



(defn history-restore-current [app-atom {:keys [snapshot diffs restoring index]}]
  (when restoring
    (reset! app-atom
      (assoc-in
        (replay-diffs snapshot (take (dec index) diffs))
        [:dev :locked] (not (= index (count diffs)))))))

(defn history-back [app-snapshot app-atom]
  ;; Don't record this xD (poor man's lock)
  (let [snapshot
        (swap! app-snapshot
          (fn [s]
            (if (pos? (:index s))
              (->
                s
                (update :index dec)
                (assoc :restoring true))
              s)))]

    (when (:restoring snapshot)
      (history-restore-current app-atom snapshot)
      (swap! app-snapshot assoc :restoring false))))

(defn history-forward [app-snapshot app-atom]
  ;; Don't record this xD (poor man's lock)
  (let [snapshot
        (swap! app-snapshot
          (fn [s]
            (if (< (:index s) (count (:diffs s)))
              (->
                s
                (update :index inc)
                (assoc :restoring true))
              s)))]

    (when (:restoring snapshot)
      (history-restore-current app-atom snapshot)
      (swap! app-snapshot assoc :restoring false))))

(defn reset-to-end [app-snapshot app-atom]
  (let [snapshot
        (swap! app-snapshot
          (fn [s]
            (->
              s
              (assoc :index (count (:diffs s)))
              (assoc :restoring true))))]

    (when (:restoring snapshot)
      (history-restore-current app-atom snapshot)
      (swap! app-snapshot assoc :restoring false))))

;; After sending history, we need to clear to a specific seq on confirmation.
(defn clear-history-to-index [app-snapshot index]
  (swap! app-snapshot
    (fn [{:keys [comms-index snapshot diffs] :as app}]
      ;; Make sure we don't clear out stuff we haven't sent.
      (let [adjusted-index (min comms-index index)]
        (if (neg? adjusted-index)
          app
          (assoc app
            :index (max 0 (- (:index app) index))
            :snapshot (replay-diffs snapshot (take adjusted-index diffs))
            :diffs (drop adjusted-index diffs)
            :comms-index (- comms-index adjusted-index)))))))

(defonce app-snapshot
  (atom
    {:index 0
     :comms-index 0
     :snapshot nil
     :comms-snapshot nil
     :diffs [] 
     :history-recording-disabled true}))

(defn comms-disabled? []
  (:comms-send-disabled @app-snapshot))

(defn setup-listener [app-atom]
  (println "Setting up history listener")
  ;; This is if we decide to roll up the history into less specific chunks
  ;; but we'll lose some data + specific ordering of actions.
  ;; multiple comms messages can shadow each other.
  ;; So we'd have to be sure this is worth it.
  #_(let [first-change (atom @app-atom)]
      (add-watch app-atom :dev/history-listener
        (fn [k r o n]
          (cmn/timeout-fn-call
            #(when (= n @app-atom)
               (let [old @first-change]
                 (reset! first-change n)
                 (record-history app-snapshot k r old n)))
            100))))

  (add-watch app-atom :dev/history-listener (partial record-history app-snapshot)))